import React, { useState, useContext, useEffect } from 'react'
import { Button, Grid, Typography, useTheme } from '@material-ui/core'
import TagsInput from 'react-tagsinput'
import { UserCommunityProfile, UserContext, UserContextType } from '../../UserContext'
import BottomFullButtonStandalone from '../../UIComponents/BottomFullButtonStandalone'
import useDebounce from '../../hooks/useDebounce'
import { persistent } from '../../services/LocalStorage'
import useMuseStyles from '../../themes/useMuseStyles'
import { useCommunityFeature } from '../../services/useCommunityFeature'
import { AppContext, AppContextType } from '../../AppContext'
import clsx from 'clsx'
import { Alert } from '@material-ui/lab'
import { MuseInput } from './CommunityCollaboratePage'

function CommunityProfileEdit({
    existingProfile = null,
    onComplete,
}: {
    existingProfile?: UserCommunityProfile | null
    onComplete?: () => void
}) {
    const { state, dispatch } = useContext(UserContext) as UserContextType
    const { dispatch: appDispatch } = useContext(AppContext) as AppContextType

    const gclasses = useMuseStyles(useTheme())

    const communityFeature = useCommunityFeature()

    const [tempProfile, set_tempProfile] = useState<UserCommunityProfile>({
        name: '',
        bio: '',
        genre: [],
        instrument: [],
        role: [],
    })
    const tempProfile_debounce = useDebounce<UserCommunityProfile>(tempProfile, 250)

    const isNew = existingProfile == null

    useEffect(() => {
        if (!existingProfile) {
            // If have temp_profile_edit available, then show that.
            const obj = persistent.getObject('temp_profile_edit')
            if (obj) {
                console.log(`Load working copy of tempProfile`)
                set_tempProfile(obj as UserCommunityProfile)
            }
        } else {
            set_tempProfile(existingProfile)
        }
    }, [])

    useEffect(() => {
        //console.log(`*** tempProfile update: %O`, tempProfile_debounce)
        persistent.set('temp_profile_edit', tempProfile)
    }, [tempProfile_debounce])

    const onCreateProfile = () => {
        communityFeature
            .createProfile(tempProfile)
            .then((result) => {
                dispatch({
                    type: 'set_user_community_profile',
                    value: tempProfile,
                })
                appDispatch({
                    type: 'root_snackbar_history_append',
                    value: {
                        severity: 'success',
                        message: 'Profile successfully created!',
                    },
                })
                persistent.delete('temp_profile_edit')
                onComplete && onComplete()
            })
            .catch((e) => {
                appDispatch({
                    type: 'root_snackbar_history_append',
                    value: {
                        severity: 'error',
                        message: 'Failed to create profile!',
                    },
                })
                onComplete && onComplete()
            })
    }

    const onUpdateProfile = () => {
        communityFeature
            .updateProfile(tempProfile)
            .then((result) => {
                dispatch({
                    type: 'update_user_community_profile',
                    value: tempProfile,
                })
                appDispatch({
                    type: 'root_snackbar_history_append',
                    value: {
                        severity: 'success',
                        message: 'Profile successfully updated!',
                    },
                })
                persistent.delete('temp_profile_edit')
                onComplete && onComplete()
            })
            .catch((e) => {
                appDispatch({
                    type: 'root_snackbar_history_append',
                    value: {
                        severity: 'error',
                        message: 'Failed to update profile!',
                    },
                })
                onComplete && onComplete()
            })
    }

    return (
        <div
            id="community-profile-edit-container"
            style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}
        >
            <Grid container style={{ padding: '0 20px' }}>
                <Grid xs={12}>
                    <Typography variant="h1" style={{ textAlign: 'center', marginBottom: '40px' }}>
                        {isNew ? 'Create Profile' : 'Update Profile'}
                    </Typography>
                </Grid>
                <Grid xs={12} sm={6} style={{ padding: '0 20px' }}>
                    <ProfileContentsLeftPanel profile={tempProfile} set_profile={set_tempProfile} />
                </Grid>
                <Grid xs={12} sm={6} style={{ padding: '0 20px' }}>
                    <ProfileContentsRightPanel profile={tempProfile} set_profile={set_tempProfile} />
                </Grid>
            </Grid>
            <Grid container>
                {/* isNew &&
                    <Grid xs={12} style={{padding:'0 25px 8px'}}>
                        <FormGroup>
                            <FormControlLabel control={<Checkbox />} label={``} />
                        </FormGroup>
                    </Grid>
                */}

                <Grid xs={12} style={{ margin: '1rem' }}>
                    {!isNew ? (
                        <Button
                            variant="outlined"
                            color="primary"
                            className={clsx(
                                gclasses.roundedMuseButton,
                                gclasses.buttonColorDangerFilled,
                                gclasses.buttonSmall
                            )}
                            classes={{ label: gclasses.roundedMuseButtonLabel }}
                            onClick={() => onComplete && onComplete()}
                            disabled={communityFeature.deleteProfileInProgress}
                        >
                            Cancel
                        </Button>
                    ) : (
                        <Alert
                            className={gclasses.alertSmall}
                            style={{ margin: '1.5rem auto', width: '26rem' }}
                            severity="info"
                        >
                            Creating a profile will allow you to search and view other peoples' profiles, and your
                            profile will also be visible to other users.
                        </Alert>
                    )}
                </Grid>
                <Grid xs={12}>
                    <BottomFullButtonStandalone
                        className={clsx(gclasses.buttonColorPrimary)}
                        onClick={isNew ? onCreateProfile : onUpdateProfile}
                        disableRipple
                        disabled={communityFeature.createProfileInProgress}
                    >
                        {' '}
                        {communityFeature.createProfileInProgress
                            ? isNew
                                ? 'CREATING PROFILE...'
                                : 'UPDATING PROFILE...'
                            : isNew
                              ? 'CREATE PROFILE'
                              : 'UPDATE PROFILE'}
                    </BottomFullButtonStandalone>
                </Grid>
            </Grid>
        </div>
    )
}

function ProfileContentsLeftPanel({
    profile,
    set_profile,
}: {
    profile: UserCommunityProfile
    set_profile: React.Dispatch<React.SetStateAction<UserCommunityProfile>>
}) {
    const { state, dispatch } = useContext(UserContext) as UserContextType

    return (
        <>
            {/*<ProfileTextEdit
                label='Username'
                value={state.profile?.username}
            />*/}
            <ProfileTextEdit
                label="Name"
                value={profile.name || ''}
                onChange={(value) => set_profile({ ...profile, name: value })}
            />
            <ProfileTextEdit
                label="Bio"
                value={profile.bio}
                rows={8}
                onChange={(value) => set_profile({ ...profile, bio: value })}
            />
        </>
    )
}

function ProfileContentsRightPanel({
    profile,
    set_profile,
}: {
    profile: any
    set_profile: React.Dispatch<React.SetStateAction<UserCommunityProfile>>
}) {
    return (
        <>
            <ProfileBadgeEdit
                label="Genre"
                value={profile.genre}
                onChange={(value) => set_profile({ ...profile, genre: value })}
            />

            <ProfileBadgeEdit
                label="Instrument"
                value={profile.instrument}
                onChange={(value) => set_profile({ ...profile, instrument: value })}
            />

            <ProfileBadgeEdit
                label="Role"
                value={profile.role}
                onChange={(value) => set_profile({ ...profile, role: value })}
            />
        </>
    )
}

function ProfileTextEdit({
    label,
    value = '',
    onChange,
    rows,
}: {
    label: string
    value?: string
    onChange?: (value: string) => void
    rows?: number
}) {
    return (
        <>
            <Typography variant="body1" style={{ marginBottom: '8px' }}>
                {label}
            </Typography>
            <MuseInput
                variant="outlined"
                value={value == null ? '' : value}
                style={{ marginBottom: '22px' }}
                onChange={(e) => {
                    onChange && onChange(e.target.value)
                }}
                multiline={!!rows}
                minRows={rows}
                disabled={onChange == null}
            />
        </>
    )
}

/**
 * view/edit/insert/remove badges
 */
function ProfileBadgeEdit({
    label,
    value = [],
    suggestions = [],
    onChange,
}: {
    label: string
    value?: string[]
    suggestions?: string[]
    onChange?: (value: string[]) => void
}) {
    return (
        <>
            <Typography variant="body1" style={{ marginBottom: '8px' }}>
                {label}
            </Typography>
            <div style={{ marginBottom: '25px' }}>
                <TagsInput
                    value={value}
                    onlyUnique={true}
                    addOnBlur={true}
                    addKeys={[9, 13, 32]} // tab, enter, space,
                    onChange={(tags, changed, changedIndexes) => {
                        //console.log('tags: %O', tags)
                        //console.log('changed: %O', changed)
                        //set_tags(tags)
                        onChange && onChange(tags)
                    }}
                />
            </div>
        </>
    )
}

export default CommunityProfileEdit
